package features.booking

import features.BaseViewModel
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import network.FirebaseClient

data class InfoRastreo(
    val ruta: String,
    val fecha: String,
    val horario: String,
    val estado: String = "no_iniciado", // no_iniciado, en_curso, finalizado
    val lat: Double = 0.0,
    val lng: Double = 0.0,
    val ultimaActualizacion: String = "",
    val pasajero: String = "",
    val asiento: Int = 0
)

class TrackingViewModel : BaseViewModel() {

    private val _infoRastreo = MutableStateFlow<InfoRastreo?>(null)
    val infoRastreo: StateFlow<InfoRastreo?> = _infoRastreo.asStateFlow()

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _error = MutableStateFlow<String?>(null)
    val error: StateFlow<String?> = _error.asStateFlow()

    private var pollingJob: Job? = null

    fun buscarViajePorCodigo(codigo: String) {
        if (codigo.isBlank()) {
            _error.value = "Ingresa un codigo de viaje"
            return
        }

        val codigoLimpio = codigo.trim()
        if (!codigoLimpio.startsWith("SMARTVAN-")) {
            _error.value = "Codigo no valido. Debe comenzar con SMARTVAN-"
            return
        }

        viewModelScope.launch {
            _isLoading.value = true
            _error.value = null
            _infoRastreo.value = null
            detenerPolling()

            try {
                val db = FirebaseClient.database
                val fechaHoy = org.example.abigail.obtenerFechaHoy()
                val fechaManana = org.example.abigail.calcularFechaManana(fechaHoy)

                // FIX: Leer solo los viajes programados (mucho más ligero que "viajes")
                // para obtener rutas + fechas + horarios activos, luego buscar el QR puntual.
                val progSnap = db.reference("viajes_programados").valueEvents.first()
                if (!progSnap.exists) {
                    _error.value = "No hay viajes registrados"
                    _isLoading.value = false
                    return@launch
                }

                var encontrado = false
                for (fecha in listOf(fechaHoy, fechaManana)) {
                    if (encontrado) break
                    progSnap.children.forEach rutaLoop@{ rutaSnap ->
                        if (encontrado) return@rutaLoop
                        val ruta = rutaSnap.key ?: return@rutaLoop

                        val fechaProgSnap = rutaSnap.child(fecha)
                        if (!fechaProgSnap.exists) return@rutaLoop

                        fechaProgSnap.children.forEach horarioLoop@{ horarioSnap ->
                            if (encontrado) return@horarioLoop
                            val horario = horarioSnap.key ?: return@horarioLoop

                            // Buscar SOLO la verificación específica (lectura puntual, no descarga masiva)
                            try {
                                val verifPath = "viajes/$ruta/$fecha/$horario/verificaciones/$codigoLimpio"
                                val verifSnap = db.reference(verifPath).valueEvents.first()
                                if (verifSnap.exists) {
                                    val pasajero = try { verifSnap.child("pasajero").value<String>() } catch (e: Exception) { "" }
                                    val asiento = try { verifSnap.child("asiento").value<Int>() } catch (e: Exception) { 0 }

                                    // Leer estado de rastreo
                                    val rastreoPath = "viajes/$ruta/$fecha/$horario/rastreo"
                                    val rastreoSnap = try {
                                        db.reference(rastreoPath).valueEvents.first()
                                    } catch (e: Exception) { null }

                                    val estado = try { rastreoSnap?.child("estado")?.value<String>() ?: "no_iniciado" } catch (e: Exception) { "no_iniciado" }
                                    val lat = try { rastreoSnap?.child("lat")?.value<Double>() ?: 0.0 } catch (e: Exception) { 0.0 }
                                    val lng = try { rastreoSnap?.child("lng")?.value<Double>() ?: 0.0 } catch (e: Exception) { 0.0 }
                                    val ultimaAct = try { rastreoSnap?.child("ultimaActualizacion")?.value<String>() ?: "" } catch (e: Exception) { "" }

                                    _infoRastreo.value = InfoRastreo(
                                        ruta = ruta,
                                        fecha = fecha,
                                        horario = horario,
                                        estado = estado,
                                        lat = lat,
                                        lng = lng,
                                        ultimaActualizacion = ultimaAct,
                                        pasajero = pasajero,
                                        asiento = asiento
                                    )
                                    encontrado = true
                                    _isLoading.value = false

                                    // Iniciar polling si el viaje esta en curso
                                    if (estado == "en_curso") {
                                        iniciarPolling(ruta, fecha, horario, pasajero, asiento)
                                    }
                                    return@horarioLoop
                                }
                            } catch (e: Exception) {
                            }
                        }
                    }
                }

                if (!encontrado) {
                    _error.value = "No se encontro un viaje con ese codigo para hoy o manana"
                }
            } catch (e: Exception) {
                _error.value = "No pudimos cargar el rastreo. Revisa tu conexión e intenta de nuevo."
            }
            _isLoading.value = false
        }
    }

    private fun iniciarPolling(ruta: String, fecha: String, horario: String, pasajero: String, asiento: Int) {
        detenerPolling()
        pollingJob = viewModelScope.launch {
            try {
                val db = FirebaseClient.database
                val rastreoPath = "viajes/$ruta/$fecha/$horario/rastreo"
                // Suscripción real-time en vez de polling — Firebase notifica cada cambio
                db.reference(rastreoPath).valueEvents.collect { snap ->
                    if (snap.exists) {
                        val estado = try { snap.child("estado").value<String>() } catch (e: Exception) { "no_iniciado" }
                        val lat = try { snap.child("lat").value<Double>() } catch (e: Exception) { 0.0 }
                        val lng = try { snap.child("lng").value<Double>() } catch (e: Exception) { 0.0 }
                        val ultimaAct = try { snap.child("ultimaActualizacion").value<String>() } catch (e: Exception) { "" }

                        _infoRastreo.value = InfoRastreo(
                            ruta = ruta,
                            fecha = fecha,
                            horario = horario,
                            estado = estado,
                            lat = lat,
                            lng = lng,
                            ultimaActualizacion = ultimaAct,
                            pasajero = pasajero,
                            asiento = asiento
                        )

                        if (estado == "finalizado") {
                            detenerPolling()
                            return@collect
                        }
                    }
                }
            } catch (e: Exception) {
                // La suscripción se cancela cuando pollingJob.cancel()
            }
        }
    }

    fun detenerPolling() {
        pollingJob?.cancel()
        pollingJob = null
    }

    fun limpiar() {
        detenerPolling()
        _infoRastreo.value = null
        _error.value = null
    }

}
